import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Play, Music, Image as ImageIcon, Clock, Eye } from 'lucide-react';
// We removed 'galleryImages' from the import below
import { mediaVideos, musicTracks } from '../mock';

// --- NEW CONFIGURATION: Define your images here ---
const galleryImages = [
  {
    id: 1,
    url: "/images/Live Performance_1.jpg", // Make sure this file exists in public/images/
    title: "Live Performance"
  },
  {
    id: 2,
    url: "/images/Live Performance_2.jpg",
    title: "Live Performance"
  },
  {
    id: 3,
    url: "/images/Crowd Interaction_1.jpg",
    title: "Crowd Interaction"
  },
  {
    id: 4,
    url: "/images/Crowd Interaction_2.jpg",
    title: "Crowd Interaction"
  },
  {
    id: 5,
    url: "/images/Moments_1.jpg",
    title: "Moments"
  },
  // Add more photos here as needed...
];

export const MediaGallery = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeTab, setActiveTab] = useState('photos'); // photos, videos, music

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="media"
      ref={ref}
      className="relative py-24 lg:py-32 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(0, 255, 136) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-7xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-purple-400 to-green-400 mb-6 uppercase">
            Media Gallery
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-green-400 via-purple-500 to-green-400 mx-auto mb-6" />
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Experience our journey through photos, videos, and music
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center gap-4 mb-12 flex-wrap"
        >
          <button
            onClick={() => setActiveTab('photos')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold uppercase tracking-wider transition-all duration-300 ${activeTab === 'photos'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-black shadow-lg shadow-green-500/50'
                : 'bg-purple-900/30 text-gray-400 hover:bg-purple-800/40 border border-purple-500/30'
              }`}
          >
            <ImageIcon className="w-5 h-5" />
            Photos
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold uppercase tracking-wider transition-all duration-300 ${activeTab === 'videos'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-black shadow-lg shadow-green-500/50'
                : 'bg-purple-900/30 text-gray-400 hover:bg-purple-800/40 border border-purple-500/30'
              }`}
          >
            <Play className="w-5 h-5" />
            Videos
          </button>
          <button
            onClick={() => setActiveTab('music')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold uppercase tracking-wider transition-all duration-300 ${activeTab === 'music'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-black shadow-lg shadow-green-500/50'
                : 'bg-purple-900/30 text-gray-400 hover:bg-purple-800/40 border border-purple-500/30'
              }`}
          >
            <Music className="w-5 h-5" />
            Music
          </button>
        </motion.div>

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {galleryImages.map((image) => (
              <motion.div
                key={image.id}
                variants={itemVariants}
                className="group relative overflow-hidden rounded-2xl aspect-square cursor-pointer"
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h4 className="text-white font-semibold text-lg">{image.title}</h4>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-2 gap-8"
          >
            {mediaVideos.map((video) => (
              <motion.div
                key={video.id}
                variants={itemVariants}
                className="group relative bg-gradient-to-br from-purple-900/30 to-gray-900/30 backdrop-blur-lg border border-purple-500/20 rounded-2xl overflow-hidden hover:border-green-400/50 transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all duration-300 flex items-center justify-center">
                    <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl shadow-green-500/50">
                      <a href={video.videoUrl} target='_blank'><Play className="w-10 h-10 text-black ml-1" fill="currentColor" /></a>
                    </div>
                  </div>
                  {/* Duration Badge */}
                  <div className="absolute bottom-4 right-4 bg-black/80 px-3 py-1 rounded-lg flex items-center gap-1">
                    <Clock className="w-4 h-4 text-green-400" />
                    <span className="text-white text-sm font-semibold">{video.duration}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-green-400 transition-colors duration-300">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">{video.views} views</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Music Tab */}
        {activeTab === 'music' && (
          <motion.div

            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="max-w-4xl mx-auto space-y-4"
          >
            {musicTracks.map((track, index) => (
              <motion.div
                key={track.id}
                variants={itemVariants}
                className="group bg-gradient-to-r from-purple-900/30 to-gray-900/30 backdrop-blur-lg border border-purple-500/20 rounded-2xl p-6 hover:border-green-400/50 transition-all duration-300 hover:scale-102 cursor-pointer"
              >
                <div className="flex items-center gap-6">
                  {/* Track Number & Play Button */}
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 bg-purple-600/30 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-all duration-300">
                      <span className="text-2xl font-bold text-gray-400 group-hover:hidden">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <a href={track.audioUrl } target='_blank'>
                        <Play className="w-8 h-8 text-black hidden group-hover:block" fill="currentColor" />
                      </a>
                    </div>
                  </div>

                  {/* Album Cover */}
                  <div className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden">
                    <img
                      src={track.coverArt}
                      alt={track.album}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-white mb-1 truncate group-hover:text-green-400 transition-colors duration-300">
                      {track.title}
                    </h3>
                    <p className="text-gray-400 text-sm truncate">{track.album}</p>
                  </div>

                  {/* Duration */}
                  <div className="flex-shrink-0 text-gray-400 font-mono text-lg">
                    {track.duration}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Placeholder Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-12 text-center"
        >

        </motion.div>
      </div>
    </section>
  );
};